<template>
  <div class="map-search-box" :class="{ 'map-search-box--embedded': embedded }">
    <!-- Desktop (sempre) e Mobile embedded: mostra o campo expandido -->
    <div class="search-input-container desktop-search">
      <input
        ref="searchInput"
        type="text"
        class="search-input"
        placeholder="Pesquisar cidade..."
        @keypress.enter="handleSearch"
      />
      <button class="search-button" @click="handleSearch" title="Pesquisar">
        <font-awesome-icon icon="fa fa-search" />
      </button>
    </div>

    <!-- Mobile: botão compacto na lateral (apenas modo fullscreen) -->
    <button
      v-if="!embedded"
      class="mobile-search-button"
      @click="toggleMobileSearch"
      title="Pesquisar cidade"
    >
      <font-awesome-icon icon="fa fa-search" />
    </button>

    <!-- Mobile: modal de busca expandido (apenas modo fullscreen) -->
    <div v-if="!embedded && mobileSearchOpen" class="mobile-search-modal" @click="mobileSearchOpen = false">
      <div class="mobile-search-content" @click.stop>
        <input
          ref="mobileSearchInput"
          type="text"
          class="mobile-search-input"
          placeholder="pesquisa cidade"
          @keypress.enter="handleMobileSearch"
        />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    map: {
      type: Object,
      default: null
    },
    embedded: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      autocomplete: null,
      mobileAutocomplete: null,
      geocoder: null,
      mobileSearchOpen: false
    };
  },
  methods: {
    initAutocomplete() {
      if (!window.google || !window.google.maps) {
        console.error('Google Maps não está carregado');
        return;
      }

      this.geocoder = new google.maps.Geocoder();

      // Configurar autocomplete
      const options = {
        types: ['(cities)'],
        componentRestrictions: { country: 'br' } // Restringir ao Brasil
      };

      this.autocomplete = new google.maps.places.Autocomplete(
        this.$refs.searchInput,
        options
      );

      // Listener para quando um lugar é selecionado
      this.autocomplete.addListener('place_changed', () => {
        const place = this.autocomplete.getPlace();

        if (!place.geometry) {
          console.warn('Nenhum detalhe disponível para: ' + place.name);
          return;
        }

        // Mover o mapa para o lugar selecionado
        if (this.map) {
          const location = place.geometry.location;
          this.map.setCenter(location);
          this.map.setZoom(12);

          // Emitir evento com os dados do lugar
          this.$emit('place-selected', {
            lat: location.lat(),
            lng: location.lng(),
            name: place.name,
            formatted_address: place.formatted_address
          });
        }
      });
    },

    toggleMobileSearch() {
      this.mobileSearchOpen = !this.mobileSearchOpen;

      if (this.mobileSearchOpen) {
        this.$nextTick(() => {
          if (this.$refs.mobileSearchInput) {
            this.$refs.mobileSearchInput.focus();
            this.initMobileAutocomplete();
          }
        });
      }
    },

    initMobileAutocomplete() {
      if (!window.google || !window.google.maps || !this.$refs.mobileSearchInput) {
        return;
      }

      const options = {
        types: ['(cities)'],
        componentRestrictions: { country: 'br' }
      };

      this.mobileAutocomplete = new google.maps.places.Autocomplete(
        this.$refs.mobileSearchInput,
        options
      );

      this.mobileAutocomplete.addListener('place_changed', () => {
        const place = this.mobileAutocomplete.getPlace();

        if (!place.geometry) {
          return;
        }

        if (this.map) {
          const location = place.geometry.location;
          this.map.setCenter(location);
          this.map.setZoom(12);

          this.$emit('place-selected', {
            lat: location.lat(),
            lng: location.lng(),
            name: place.name,
            formatted_address: place.formatted_address
          });
        }

        this.mobileSearchOpen = false;
      });
    },

    handleSearch() {
      const searchTerm = this.$refs.searchInput.value.trim();

      if (!searchTerm) {
        return;
      }

      if (!this.geocoder) {
        this.geocoder = new google.maps.Geocoder();
      }

      // Geocodificar o termo de busca
      this.geocoder.geocode(
        {
          address: searchTerm,
          componentRestrictions: { country: 'br' }
        },
        (results, status) => {
          if (status === 'OK' && results[0]) {
            const location = results[0].geometry.location;

            if (this.map) {
              this.map.setCenter(location);
              this.map.setZoom(12);

              this.$emit('place-selected', {
                lat: location.lat(),
                lng: location.lng(),
                name: results[0].address_components[0]?.long_name || searchTerm,
                formatted_address: results[0].formatted_address
              });
            }
          } else {
            alert('Localização não encontrada. Tente novamente.');
          }
        }
      );
    },

    handleMobileSearch() {
      const searchTerm = this.$refs.mobileSearchInput.value.trim();

      if (!searchTerm) {
        return;
      }

      if (!this.geocoder) {
        this.geocoder = new google.maps.Geocoder();
      }

      this.geocoder.geocode(
        {
          address: searchTerm,
          componentRestrictions: { country: 'br' }
        },
        (results, status) => {
          if (status === 'OK' && results[0]) {
            const location = results[0].geometry.location;

            if (this.map) {
              this.map.setCenter(location);
              this.map.setZoom(12);

              this.$emit('place-selected', {
                lat: location.lat(),
                lng: location.lng(),
                name: results[0].address_components[0]?.long_name || searchTerm,
                formatted_address: results[0].formatted_address
              });
            }

            this.mobileSearchOpen = false;
          } else {
            alert('Localização não encontrada. Tente novamente.');
          }
        }
      );
    }
  },
  mounted() {
    // Aguardar o Google Maps estar carregado
    const checkGoogleMaps = setInterval(() => {
      if (window.google && window.google.maps && window.google.maps.places) {
        clearInterval(checkGoogleMaps);
        this.initAutocomplete();
      }
    }, 100);

    // Timeout de segurança
    setTimeout(() => {
      clearInterval(checkGoogleMaps);
    }, 5000);
  },
  watch: {
    map(newMap) {
      if (newMap && !this.autocomplete) {
        this.initAutocomplete();
      }
    }
  }
};
</script>

<style scoped>
.map-search-box {
  position: absolute;
  top: 10px;
  left: 10px;
  transform: none;
  z-index: 1000;
  pointer-events: all;
  width: auto;
  max-width: calc(100% - 20px);
}

.desktop-search {
  display: flex;
}

.mobile-search-button {
  display: none;
}

.mobile-search-modal {
  display: none;
}

.search-input-container {
  display: flex;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.search-input {
  border: none;
  padding: 12px 16px;
  font-size: 14px;
  width: 300px;
  outline: none;
}

.search-input::placeholder {
  color: #999;
}

.search-button {
  background: #006035;
  color: white;
  border: none;
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.3s;
  outline: none;
}

.search-button:hover {
  background: #004d2a;
}

.search-button svg {
  font-size: 16px;
}

/* Responsivo para mobile - modo fullscreen (padrão) */
@media (max-width: 767px) {
  .map-search-box:not(.map-search-box--embedded) {
    position: static;
    transform: none;
    max-width: none;
  }

  .map-search-box:not(.map-search-box--embedded) .desktop-search {
    display: none;
  }

  .map-search-box:not(.map-search-box--embedded) .mobile-search-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 50px;
    height: 50px;
    background: white;
    border: none;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    cursor: pointer;
    position: fixed;
    top: 340px;
    right: 10px;
    z-index: 1000;
  }

  .map-search-box:not(.map-search-box--embedded) .mobile-search-button svg {
    font-size: 18px;
    color: #006035;
  }

  .map-search-box:not(.map-search-box--embedded) .mobile-search-modal {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 99999 !important;
  }

  .mobile-search-content {
    position: absolute;
    top: 15%;
    left: 50%;
    transform: translateX(-50%);
    width: calc(100% - 40px);
    max-width: 400px;
    background: white;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    z-index: 100000 !important;
  }

  .mobile-search-input {
    width: 100%;
    border: 1px solid #ddd;
    border-radius: 8px;
    padding: 12px 16px;
    font-size: 14px;
    outline: none;
  }

  .mobile-search-input:focus {
    border-color: #006035;
  }

  /* Modo embedded: mantém o campo inline no mobile */
  .map-search-box--embedded {
    position: absolute;
    top: 10px;
    left: 10px;
    right: 10px;
    max-width: none;
    z-index: 1000;
  }

  .map-search-box--embedded .desktop-search {
    display: flex;
    width: 100%;
  }

  .map-search-box--embedded .search-input {
    width: 100%;
    min-width: 0;
    font-size: 13px;
    padding: 10px 12px;
  }

  .map-search-box--embedded .search-button {
    padding: 10px 14px;
  }
}
</style>

<!-- Estilo global para o dropdown do Google Places Autocomplete (injetado no body) -->
<style>
.pac-container {
  border-radius: 8px;
  margin-top: 4px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  font-family: inherit;
  z-index: 100000 !important;
}
</style>
